import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { alleDokumente } from "../../windowManager/browserFenster/fensterRegister";

/**
 * „Was kam heraus?" — der Dialog beim Erledigen eines Vorgangs (PO 09.09.2026:
 * „wenn ein Vorgang erledigt, dann Zusammenfassungs-Popup, kein Fluent bitte").
 *
 * Deshalb nach dem Muster von `redesign/ConfirmDialog`: Portal an `document.body`,
 * REFINED-Tokens inline (außerhalb des `.kvp-rd`-Scopes griffe keine Klasse),
 * ESC und Backdrop schließen.
 *
 * **Überspringen ist erlaubt.** Der Abschluss ist bereits gespeichert, wenn
 * dieser Dialog erscheint — er fragt NACH, er hält nichts auf. Ein Pflichtfeld
 * an dieser Stelle würde eine Mautstelle aus dem häufigsten Handgriff machen,
 * und die Antwort wäre dann ein Punkt.
 *
 * Derselbe Dialog dient dem späteren ÄNDERN über die Zusammenfassungs-Karte;
 * `wert` bringt dann den Bestand mit.
 */
export const ZusammenfassungDialog: React.FC<{
    /** Betreff des Vorgangs — im Kopf, damit klar ist, worüber gesprochen wird. */
    betreff: string;
    wert: string;
    /** Beim Ändern eines Bestands lautet die Frage anders als beim Abschluss. */
    modus?: "abschluss" | "aendern";
    /**
     * Überschreibt die Überschrift. Gebraucht seit dem 13.09.2026 vom To-do:
     * derselbe Dialog fragt dort dasselbe, aber „Vorgang erledigt“ wäre die
     * falsche Sache — erledigt ist das To-do.
     */
    titel?: string;
    onSpeichern: (text: string) => void | Promise<void>;
    onSchliessen: () => void;
}> = ({ betreff, wert, modus = "abschluss", titel: titelVorgabe, onSpeichern, onSchliessen }) => {
    const [text, setText] = useState(wert);
    const [busy, setBusy] = useState(false);
    const feldRef = useRef<HTMLTextAreaElement | null>(null);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !busy) onSchliessen();
        };
        alleDokumente.addEventListener("keydown", onKey);
        // Der Fokus gehört ins Textfeld, nicht auf einen Knopf: der Dialog
        // fragt etwas, er lässt nicht bestätigen.
        const t = window.setTimeout(() => feldRef.current?.focus(), 0);
        return () => {
            alleDokumente.removeEventListener("keydown", onKey);
            window.clearTimeout(t);
        };
    }, [busy, onSchliessen]);

    const speichern = async () => {
        setBusy(true);
        try {
            await onSpeichern(text.trim());
        } catch (err) {
            // Offen bleiben und wieder bedienbar werden: der getippte Text ist
            // sonst weg, und der Nutzer weiß nicht einmal, dass nichts ankam.
            // Die Seite meldet den Fehler selbst in ihrer Kopfzeile.
            console.error("[ZusammenfassungDialog] Speichern fehlgeschlagen", err);
            setBusy(false);
            return;
        }
        // Kein `setBusy(false)` danach — der Aufrufer nimmt den Dialog jetzt
        // aus dem Baum, ein Zustandswechsel liefe ins Leere.
        onSchliessen();
    };

    const titel =
        titelVorgabe ?? (modus === "aendern" ? "Zusammenfassung ändern" : "Vorgang erledigt — was kam heraus?");

    return createPortal(
        <div
            role="presentation"
            onMouseDown={() => {
                if (!busy) onSchliessen();
            }}
            style={{
                position: "fixed",
                inset: 0,
                zIndex: 100002,
                background: "rgba(20,8,20,0.34)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 24,
                fontFamily: REFINED.fontUI,
            }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-label={titel}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                    width: "min(560px, 100%)",
                    background: REFINED.white,
                    border: `1px solid ${REFINED.line}`,
                    borderRadius: 14,
                    boxShadow: REFINED.cardShadow,
                    overflow: "hidden",
                    color: REFINED.ink,
                }}
            >
                <div style={{ padding: "15px 18px", borderBottom: `2px solid ${REFINED.goldLine}` }}>
                    <h3 style={{ margin: 0, fontSize: REFINED.fs.lg, fontWeight: 800 }}>{titel}</h3>
                    <div
                        style={{
                            marginTop: 3,
                            fontSize: REFINED.fs.xs,
                            color: REFINED.ink3,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {betreff}
                    </div>
                </div>

                <div style={{ padding: "16px 18px" }}>
                    <label
                        htmlFor="vg3-zus-feld"
                        style={{
                            display: "block",
                            marginBottom: 7,
                            fontSize: REFINED.fs.sm,
                            color: REFINED.ink2,
                            lineHeight: 1.5,
                        }}
                    >
                        Ein paar Sätze für den, der die Akte später öffnet: was entschieden wurde,
                        was vereinbart ist, was offen bleibt.
                    </label>
                    <textarea
                        id="vg3-zus-feld"
                        ref={feldRef}
                        rows={6}
                        value={text}
                        disabled={busy}
                        onChange={(e) => setText(e.target.value)}
                        style={{
                            width: "100%",
                            boxSizing: "border-box",
                            resize: "vertical",
                            font: "inherit",
                            fontSize: REFINED.fs.md,
                            lineHeight: 1.55,
                            color: REFINED.ink,
                            padding: "10px 12px",
                            borderRadius: 10,
                            border: `1px solid ${REFINED.line}`,
                            background: REFINED.white,
                            outline: "none",
                        }}
                    />
                </div>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 10,
                        padding: "12px 18px",
                        borderTop: `1px solid ${REFINED.line}`,
                        background: REFINED.fieldBg,
                    }}
                >
                    <button
                        type="button"
                        onClick={onSchliessen}
                        disabled={busy}
                        style={{
                            appearance: "none",
                            font: "inherit",
                            fontSize: REFINED.fs.sm,
                            fontWeight: 700,
                            padding: "8px 16px",
                            borderRadius: 10,
                            cursor: busy ? "not-allowed" : "pointer",
                            background: REFINED.white,
                            border: `1px solid ${REFINED.aub100}`,
                            color: REFINED.aub,
                            opacity: busy ? 0.55 : 1,
                        }}
                    >
                        {modus === "aendern" ? "Abbrechen" : "Später"}
                    </button>
                    <button
                        type="button"
                        onClick={() => void speichern()}
                        disabled={busy}
                        style={{
                            appearance: "none",
                            font: "inherit",
                            fontSize: REFINED.fs.sm,
                            fontWeight: 700,
                            padding: "8px 16px",
                            borderRadius: 10,
                            cursor: busy ? "not-allowed" : "pointer",
                            background: REFINED.aub,
                            border: `1px solid ${REFINED.aub}`,
                            color: REFINED.white,
                            opacity: busy ? 0.6 : 1,
                        }}
                    >
                        {busy ? "…" : "Speichern"}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};
